/**
 * WordPress dependencies
 */
import { dispatch, useSelect } from '@safe-wordpress/data';

/**
 * External dependencies
 */
import { cloneDeep, kebabCase, mapValues, omit } from 'lodash';
import { calculate as getSelectorPriority } from 'specificity';

import { CssTypes } from '@adobe/css-tools';
import { store as NAB_DATA } from '@nab/data';
import { store as NAB_EDITOR } from '@nab/editor';

import type { CssStylesheetAST } from '@adobe/css-tools';
import type {
	Alternative,
	AlternativeId,
	CssActiveElement,
	CssProp,
	CssPropName,
	CssVisualEditorState,
	Maybe,
} from '@nab/types';

/**
 * Internal dependencies
 */
import {
	appendFallbackRule,
	appliesToSelection,
	isOverwrittenDeclaration,
	cleanAst,
	getLastRuleIndex as doGetLastRuleIndex,
	stringifyAst,
} from '../../utils/ast';
import { combinePropsForAst } from '../../utils/combine-props-for-ast';
import { byName, expandProp, expandPropName } from '../../utils/props';
import { useCallback } from '@safe-wordpress/element';

type CssPropUpdate = CssProp & {
	readonly isRemovable: boolean;
};

export const useVisualEditorState = (): Maybe< CssVisualEditorState > => {
	const state = useSelect(
		( select ) =>
			select( NAB_DATA ).getPageAttribute( 'css-editor/cssEditorState' ),
		[]
	);
	return !! state?.activeElement?.selector && state?.mode === 'visual-editor'
		? state
		: undefined;
};

export const usePropManager = (): ( (
	value: Partial< Record< CssPropName, Maybe< string > > >
) => void ) => {
	const state = useVisualEditorState();
	const alternative = useAlternative( state?.alternativeId );

	return useCallback(
		( values: Partial< Record< CssPropName, Maybe< string > > > ) => {
			if (
				! alternative ||
				! hasDetails( state ) ||
				! hasActiveElement( state )
			) {
				return;
			}

			const workingAst = getWorkingAst( values, state );
			const selector = state.activeElement.selector;
			const priority = getSelectorPriority( selector );
			const ruleIndex = getLastRuleIndex( workingAst, selector );

			const workingProps = getWorkingProps( values, state );
			const updatedProps = mapValues(
				values,
				( value, name ): CssPropUpdate => ( {
					type: 'regular',
					name: name as CssPropName,
					value: value ?? '',
					base: undefined,
					ruleIndex,
					selectors: [ selector ],
					priority,
					isRemovable: undefined === value,
				} )
			);

			const newProps = mergeUpdatedProps(
				workingProps,
				updatedProps,
				state.details.propsRequiringImportant
			);
			const ast = updateAst( newProps, state.activeElement, workingAst );

			updateAlternative( alternative, ast );
			updateEditor(
				state,
				ast,
				newProps,
				state.details.propsRequiringImportant
			);
		},
		[ state, alternative ]
	);
};

// =======
// HELPERS
// =======

const useAlternative = ( alternativeId?: AlternativeId ) =>
	useSelect(
		( select ) =>
			alternativeId
				? select( NAB_EDITOR ).getAlternative( alternativeId )
				: undefined,
		[ alternativeId ]
	);

const hasActiveElement = < T extends CssVisualEditorState >(
	state: Maybe< T >
): state is T & Pick< Required< T >, 'activeElement' > =>
	!! state?.activeElement;

const hasDetails = < T extends CssVisualEditorState >(
	state: Maybe< T >
): state is T & Pick< Required< T >, 'details' > => !! state?.details;

function getWorkingAst(
	updates: Partial< Record< CssPropName, Maybe< string > > >,
	state: Required< CssVisualEditorState >
): CssStylesheetAST {
	return removeProps(
		Object.keys( updates ) as CssPropName[],
		state.activeElement,
		appendFallbackRule( state.details.ast, state.activeElement.selector )
	);
}

function getWorkingProps(
	updates: Partial< Record< CssPropName, Maybe< string > > >,
	state: Required< CssVisualEditorState >
): Partial< Record< CssPropName, CssProp > > {
	const updatingPropNames = Object.keys( updates ).map(
		( n ) => n as CssPropName
	);
	const names = [
		...updatingPropNames.flatMap( expandPropName ),
		...updatingPropNames,
	];
	return byName(
		Object.values( state.details.props ).filter(
			( p ) =>
				'readonly' === p.type || !! p.base || ! names.includes( p.name )
		)
	);
}

function getLastRuleIndex( ast: CssStylesheetAST, selector: string ): number {
	const fallbackIndex = ast.stylesheet.rules.length - 1;
	return doGetLastRuleIndex( ast, selector, fallbackIndex );
}

function mergeUpdatedProps(
	original: Partial< Record< CssPropName, CssProp > >,
	updates: Partial< Record< CssPropName, CssPropUpdate > >,
	propsRequiringImportant: ReadonlyArray< CssPropName >
): Partial< Record< CssPropName, CssProp > > {
	const res = Object.values( updates )
		.flatMap( expandProp )
		.reduce( ( result, prop ) => {
			const existing = result[ prop.name ];
			if ( 'readonly' === existing?.type ) {
				return result;
			}

			prop =
				'readonly' === prop.type
					? prop
					: {
							...prop,
							type: propsRequiringImportant.includes( prop.name )
								? 'important'
								: prop.type,
					  };

			const regularProp = omit( prop, 'isRemovable' ) as CssProp;
			if ( ! existing || ! existing.base ) {
				return prop.isRemovable
					? result
					: { ...result, [ prop.name ]: regularProp };
			}

			const updatedProp = {
				...existing,
				value: prop.isRemovable ? '' : prop.value,
				base: existing.base,
			} as CssProp;
			return { ...result, [ prop.name ]: updatedProp };
		}, original );
	return res;
}

function removeProps(
	propNames: ReadonlyArray< CssPropName >,
	activeElement: CssActiveElement,
	ast: CssStylesheetAST
): CssStylesheetAST {
	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: ast.stylesheet.rules.map( ( r ) =>
				appliesToSelection( activeElement, r )
					? {
							...r,
							declarations: r.declarations.filter(
								( d ) =>
									! isOverwrittenDeclaration( propNames, d )
							),
					  }
					: r
			),
		},
	};
}

function updateAst(
	props: Partial< Record< CssPropName, CssProp > >,
	activeElement: CssActiveElement,
	ast: CssStylesheetAST
): CssStylesheetAST {
	ast = cloneDeep( ast );
	combinePropsForAst(
		Object.values( props ).filter( ( p ) => 'readonly' !== p.type )
	)
		.filter( ( prop ) => prop.value !== prop.base?.value )
		.forEach( ( prop ) => {
			const rule = ast.stylesheet.rules[ prop.ruleIndex ];
			if ( ! appliesToSelection( activeElement, rule ) ) {
				return;
			}
			const value = prop.value;
			rule.declarations.push( {
				type: CssTypes.declaration,
				property: kebabCase( prop.name ),
				value:
					value && 'important' === prop.type
						? `${ value } !important`
						: value,
			} );
		} );
	return cleanAst( ast );
}

function updateAlternative( alternative: Alternative, ast: CssStylesheetAST ) {
	void dispatch( NAB_EDITOR ).setAlternative( alternative.id, {
		...alternative,
		attributes: {
			...alternative.attributes,
			css: stringifyAst( cleanAst( ast ) ),
		},
	} );
}

function updateEditor(
	state: CssVisualEditorState,
	ast: CssStylesheetAST,
	props: Partial< Record< CssPropName, CssProp > >,
	propsRequiringImportant: ReadonlyArray< CssPropName >
) {
	void dispatch( NAB_DATA ).setPageAttribute( 'css-editor/cssEditorState', {
		...state,
		mode: 'visual-editor',
		details: {
			ast: cleanAst( ast ),
			props,
			propsRequiringImportant,
		},
	} );
}
